import { useState } from 'react'
import { Button } from '@shared/ui/Button'
import { usePlatform, prepareAsyncOpenTab } from '@shared/platform'
import { useAppStore } from '@/store/useAppStore'
import {
  useSetAutoRenewMutation,
  useSubscriptionPricesQuery,
  usePlanLookup,
  formatPrice,
  CancelAutoRenewConfirmModal,
  AutoRenewDisabledResultModal,
} from '@features/subscription'
import {
  ProviderPickerModal,
  useBuySubscriptionMutation,
  usePaymentMethodsQuery,
  useStartPayment,
  type ProviderPickerSubmit,
} from '@features/payments'
import RecommendedChannels from '../RecommendedChannels'
import { PaymentMethodsBlock } from './PaymentMethodsBlock'
import styles from './SubscriptionSection.module.css'

interface SubscriptionSectionProps {
  onSettingsClick: () => void
}

interface ActiveDateRowProps {
  formattedExpiresAt: string | null
  autoRenew: boolean
  showToggle: boolean
  toggleDisabled: boolean
  onToggle: () => void
}

const ActiveDateRow = ({
  formattedExpiresAt,
  autoRenew,
  showToggle,
  toggleDisabled,
  onToggle,
}: ActiveDateRowProps) => (
  <div className={styles.activeRow}>
    <div className={styles.activeColumn}>
      <span className={styles.activeLabel}>Активна до:</span>
      <span className={styles.activeDate}>{formattedExpiresAt ?? '—'}</span>
    </div>
    {showToggle && (
      <div className={styles.activeColumn}>
        <span className={styles.activeLabel}>Автопродление</span>
        <button
          type="button"
          className={`${styles.autoRenewToggle} ${autoRenew ? styles.autoRenewToggleActive : ''}`}
          aria-pressed={autoRenew}
          aria-label="Автопродление"
          onClick={onToggle}
          disabled={toggleDisabled}
        >
          <span className={styles.autoRenewKnob} aria-hidden />
        </button>
      </div>
    )}
  </div>
)

const getAutoRenewErrorText = (error: unknown): string => {
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status === 409)
    return 'Нет сохранённой карты для автопродления. Сначала привяжите карту.'
  if (status === 502)
    return 'Провайдер не отвечает. Попробуйте ещё раз через минуту.'
  return 'Не удалось изменить автопродление. Попробуйте ещё раз.'
}

const getBuyErrorText = (error: unknown): string => {
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status === 409)
    return 'У вас уже есть активная подписка или незавершённый платёж.'
  if (status === 502)
    return 'Не удалось связаться с провайдером. Попробуйте ещё раз.'
  return 'Не удалось создать платёж. Попробуйте ещё раз.'
}

const SubscriptionSection = ({ onSettingsClick }: SubscriptionSectionProps) => {
  const subscription = useAppStore(s => s.subscription)
  const hasActiveSubscription = useAppStore(s => s.hasActiveSubscription)
  const isActive = hasActiveSubscription()
  const setAutoRenewMutation = useSetAutoRenewMutation()
  const autoRenewError = setAutoRenewMutation.isError
    ? getAutoRenewErrorText(setAutoRenewMutation.error)
    : null
  const { lookup: planLookup } = usePlanLookup()
  const plan = planLookup(subscription.subscriptionInfoId)
  // Закреплённая цена приоритетнее тарифной: бэк фиксирует её при оформлении
  // и не меняет, даже если плановая цена поднялась. Если закреплённой нет —
  // fallback на текущую цену тарифа из справочника.
  const tariffPrice =
    subscription.renewalPrice != null
      ? formatPrice(subscription.renewalPrice, subscription.renewalCurrency ?? 'RUB')
      : plan
        ? formatPrice(plan.price, plan.currency)
        : null
  const tariffLabel =
    tariffPrice != null && plan?.suffix
      ? `${tariffPrice} / ${plan.suffix}`
      : tariffPrice
  const [isCancelOpen, setCancelOpen] = useState(false)
  const [isResultOpen, setResultOpen] = useState(false)
  const [resultMode, setResultMode] = useState<'enabled' | 'disabled'>(
    'disabled'
  )

  const [isBuyOpen, setBuyOpen] = useState(false)
  const [buyError, setBuyError] = useState<string | null>(null)
  const { data: pricesData } = useSubscriptionPricesQuery(isBuyOpen)
  const buyMutation = useBuySubscriptionMutation()
  const startPayment = useStartPayment()
  const platform = usePlatform()
  const { data: paymentMethodsData } = usePaymentMethodsQuery()
  const paymentMethods = paymentMethodsData?.methods ?? []
  // Карта для автопродления: основная, иначе первая привязанная.
  const autoRenewCard =
    paymentMethods.find(m => m.is_main) ?? paymentMethods[0] ?? null
  // Тоггл автопродления — только для Robokassa: в Tribute управление подпиской
  // живёт в их собственном Telegram-приложении, наш PATCH туда не дотягивается.
  const isRobokassa = subscription.provider === 'robokassa'
  const isTribute = subscription.provider === 'tribute'
  const hasMainCard = isRobokassa && autoRenewCard != null

  const openTributeManage = () => {
    platform.openLink('https://t.me/tribute/app')
  }

  const handleToggleAutoRenew = () => {
    if (setAutoRenewMutation.isLoading) return
    if (subscription.autoRenew) {
      // Выключаем — открываем модалку подтверждения
      setAutoRenewMutation.reset()
      setCancelOpen(true)
    } else {
      // Включаем — бэкенду нужен payment_method_id, иначе 409 «нет карты».
      if (!autoRenewCard) return
      setAutoRenewMutation.reset()
      setAutoRenewMutation.mutate(
        { enabled: true, payment_method_id: autoRenewCard.payment_method_id },
        {
          onSuccess: () => {
            setResultMode('enabled')
            setResultOpen(true)
          },
        }
      )
    }
  }

  const openBuyModal = () => {
    setBuyError(null)
    buyMutation.reset()
    setBuyOpen(true)
  }
  const closeBuyModal = () => {
    if (buyMutation.isLoading) return
    setBuyError(null)
    buyMutation.reset()
    setBuyOpen(false)
  }
  const handleBuySubmit = ({
    planId,
    provider,
    saveCard,
    paymentMethodId,
  }: ProviderPickerSubmit) => {
    setBuyError(null)
    // Окно открываем синхронно по клику — после await mutate браузер блокирует popup.
    const tab = prepareAsyncOpenTab(platform)
    buyMutation.mutate(
      {
        subscription_info_id: planId,
        provider,
        save_card: saveCard,
        payment_method_id: paymentMethodId,
      },
      {
        onSuccess: invoice => {
          setBuyOpen(false)
          startPayment(invoice, tab)
        },
        onError: error => {
          tab.abort()
          setBuyError(getBuyErrorText(error))
        },
      }
    )
  }

  const closeCancelModal = () => {
    if (setAutoRenewMutation.isLoading) return
    setCancelOpen(false)
  }
  const handleConfirmCancel = () => {
    setAutoRenewMutation.mutate(
      { enabled: false },
      {
        onSuccess: () => {
          setCancelOpen(false)
          setResultMode('disabled')
          setResultOpen(true)
        },
      }
    )
  }

  const formattedExpiresAt = subscription.expiresAt
    ? new Date(subscription.expiresAt).toLocaleDateString('ru-RU')
    : null

  // Модалки автопродления держим вне веток состояния: смена autoRenew
  // меняет ветку (early return), и если рендерить их только в одной —
  // result-модалка пропадёт сразу после успешной мутации.
  const autoRenewModals = (
    <>
      {isCancelOpen && (
        <CancelAutoRenewConfirmModal
          expiresAtFormatted={formattedExpiresAt}
          isSubmitting={setAutoRenewMutation.isLoading}
          errorText={autoRenewError}
          onConfirm={handleConfirmCancel}
          onClose={closeCancelModal}
        />
      )}

      {isResultOpen && (
        <AutoRenewDisabledResultModal
          expiresAtFormatted={formattedExpiresAt}
          mode={resultMode}
          onClose={() => setResultOpen(false)}
        />
      )}
    </>
  )

  // Состояние «Подписка неактивна»: либо нет подписки, либо `is_active=false`,
  // либо срок уже прошёл (вычисляется в `hasActiveSubscription`).
  if (!isActive) {
    return (
      <>
        <div className={`profile-section ${styles.section}`}>
          <h2 className="section-title">Подписка</h2>
          <p className={styles.statusText}>Подписка неактивна</p>
          <p className={styles.description}>
            Оформите подписку, чтобы получить доступ ко всему контенту.
          </p>
        </div>
        <PaymentMethodsBlock />

        <Button onClick={openBuyModal} block>
          Оформить подписку
        </Button>
        <button className="support-button" onClick={onSettingsClick}>
          Тех. поддержка
        </button>
        <RecommendedChannels defaultOpen={true} />

        {isBuyOpen && (
          <ProviderPickerModal
            plans={pricesData?.subscriptions ?? []}
            paymentMethods={paymentMethods}
            onSubmit={handleBuySubmit}
            onClose={closeBuyModal}
            isSubmitting={buyMutation.isLoading}
            errorText={buyError}
          />
        )}
        {autoRenewModals}
      </>
    )
  }

  // Состояние «Подписка активна, автопродление отключено».
  // Подписка работает до `expiresAt`, но дальше не продлится — нужна кнопка «Возобновить».
  if (subscription.autoRenew === false) {
    return (
      <>
        <div className={`profile-section ${styles.section}`}>
          <h2 className="section-title">Подписка</h2>
          <ActiveDateRow
            formattedExpiresAt={formattedExpiresAt}
            autoRenew={false}
            showToggle={hasMainCard}
            toggleDisabled={setAutoRenewMutation.isLoading}
            onToggle={handleToggleAutoRenew}
          />
          {tariffLabel && (
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Тариф</span>
              <span className={styles.metaValue}>{tariffLabel}</span>
            </div>
          )}
          <p className={styles.description}>
            Автопродление отключено. После окончания срока доступ к подписке
            прекратится.
          </p>
          {autoRenewError && <p className={styles.error}>{autoRenewError}</p>}
        </div>
        <PaymentMethodsBlock />
        <button className="support-button" onClick={onSettingsClick}>
          Тех. поддержка
        </button>
        <RecommendedChannels defaultOpen={true} />
        {autoRenewModals}
      </>
    )
  }

  // Состояние «Подписка активна, автопродление включено».
  return (
    <>
      <div className={`profile-section ${styles.section}`}>
        <h2 className="section-title">Подписка</h2>

        <ActiveDateRow
          formattedExpiresAt={formattedExpiresAt}
          autoRenew={true}
          showToggle={hasMainCard}
          toggleDisabled={setAutoRenewMutation.isLoading}
          onToggle={handleToggleAutoRenew}
        />

        {tariffLabel && (
          <div className={styles.metaRow}>
            <span className={styles.metaLabel}>Тариф</span>
            <span className={styles.metaValue}>{tariffLabel}</span>
          </div>
        )}

        {formattedExpiresAt && (
          <div className={styles.metaRow}>
            <span className={styles.metaLabel}>Следующее списание</span>
            <span className={styles.metaValue}>{formattedExpiresAt}</span>
          </div>
        )}

        {autoRenewError && <p className={styles.error}>{autoRenewError}</p>}
      </div>
      <PaymentMethodsBlock />

      {isTribute && (
        <button className="support-button" onClick={openTributeManage}>
          Управлять подпиской
        </button>
      )}
      <button className="support-button" onClick={onSettingsClick}>
        Тех. поддержка
      </button>
      <RecommendedChannels defaultOpen={true} />
      {autoRenewModals}
    </>
  )
}

export default SubscriptionSection
